/**
 * @fileoverview ProcesoProveedorRepository - Repositorio para la gestión de proveedores de proceso
 */

import { DataRepository } from './base/DataRepository';
import type {
  ProcesoProveedor,
  ProcesoProveedorExpanded,
  CreateProcesoProveedorData,
  UpdateProcesoProveedorData,
} from '../types/collections/ProcesoProveedor';
import type { ListResult } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Repositorio para la colección `proceso_proveedor`
 */
export class ProcesoProveedorRepository extends DataRepository<ProcesoProveedor> {
  private logger = createModuleLogger('ProcesoProveedorRepository');

  constructor() {
    super('proceso_proveedor');
  }

  /**
   * Obtiene todos los proveedores activos
   */
  async getAllActive(): Promise<ProcesoProveedor[]> {
    try {
      // Probar diferentes formatos de filtro para compatibilidad
      // Primero intentar con 'activo = true' (formato estándar)
      let result = await this.pb.collection(this.collectionName).getFullList<ProcesoProveedor>({
        filter: 'activo = true',
        sort: 'nombre',
      });
      
      // Si no hay resultados, intentar sin filtro para ver todos los registros
      if (result.length === 0) {
        this.logger.debug('No se encontraron proveedores con filtro activo = true, intentando sin filtro');
        result = await this.pb.collection(this.collectionName).getFullList<ProcesoProveedor>({
          sort: 'nombre',
        });
        // Filtrar manualmente los activos
        result = result.filter(p => p.activo === true);
      }
      
      this.logger.debug('Proveedores activos obtenidos', { count: result.length });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener proveedores activos: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_ACTIVE_ERROR',
        error
      );
    }
  }

  /**
   * Busca proveedores por nombre (búsqueda parcial)
   */
  async searchByName(searchTerm: string): Promise<ProcesoProveedor[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<ProcesoProveedor>({
        filter: `nombre ~ "${searchTerm}"`,
        sort: 'nombre',
      });
      this.logger.debug('Búsqueda de proveedores por nombre', { searchTerm, count: result.length });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al buscar proveedores por nombre "${searchTerm}": ${error instanceof Error ? error.message : String(error)}`,
        'SEARCH_BY_NAME_ERROR',
        error
      );
    }
  }

  /**
   * Lista con paginación y filtros opcionales
   */
  async getListWithFilters(
    page: number = 1,
    pageSize: number = 10,
    filters: { search?: string; activo?: boolean } = {}
  ): Promise<ListResult<ProcesoProveedor>> {
    try {
      const filterParts: string[] = [];

      if (filters.search) {
        const term = filters.search.replace(/"/g, '\\"');
        filterParts.push(`(nombre ~ "${term}" || correo ~ "${term}" || nit ~ "${term}")`);
      }

      if (filters.activo !== undefined) {
        filterParts.push(`activo=${filters.activo}`);
      }

      const filter = filterParts.length > 0 ? filterParts.join(' && ') : '';

      const result = await this.pb.collection(this.collectionName).getList<ProcesoProveedor>(page, pageSize, {
        filter,
        sort: 'nombre',
      });

      this.logger.debug('Listado de proveedores con filtros', {
        page,
        pageSize,
        filters,
        totalItems: result.totalItems,
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener lista de proveedores: ${error instanceof Error ? error.message : String(error)}`,
        'GET_LIST_WITH_FILTERS_ERROR',
        error
      );
    }
  }

  /**
   * Desactiva un proveedor (soft delete)
   */
  async deactivate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<ProcesoProveedor>(id, { activo: false });
      this.logger.info('Proveedor desactivado', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al desactivar proveedor ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'DEACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Activa un proveedor
   */
  async activate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<ProcesoProveedor>(id, { activo: true });
      this.logger.info('Proveedor activado', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al activar proveedor ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'ACTIVATE_ERROR',
        error
      );
    }
  }
}